"use client"
import { FONT_VARIANT } from "@/lib/constants";
import { Loader2, Mail } from "lucide-react";
import { Phone } from "lucide-react";
import { MapPin } from "lucide-react";
import { Instagram } from "lucide-react";
import { Facebook } from "lucide-react";
import Image from "next/image";
import { sendContactForm } from "@/app/actions";
import { OpeningHours } from "@/components/opening-hours";
import { useFormStatus } from "react-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useInView } from "@/hooks/use-in-view";

function SubmitButton() {
    const { pending } = useFormStatus()

    return (
        <Button
            type="submit"
            className="bg-white hover:bg-white/90 text-black rounded-none"
            disabled={pending}
        >
            {pending ? (
                <>
                    <Loader2 className="animate-spin mt-3" />
                    <span>Wird gesendet...</span>
                </>
            ) : (
                'Anfrage verschicken'
            )}
        </Button>
    )
}
export default function Contact() {
    const { toast } = useToast()
    const [infoRef, infoInView] = useInView({ threshold: 0.3 });
    const [formRef, formInView] = useInView({ threshold: 0.2 });
    // Anti-spam: set after mount so bots posting the raw HTML get no timestamp
    const [formStartedAt, setFormStartedAt] = useState('')
    useEffect(() => {
        setFormStartedAt(Date.now().toString())
    }, [])

    return (
        <section id="contact" className="my-20 md:my-40 py-20 md:py-28 text-white relative">
            <div className="container space-y-96">
                {/* First Row: Business Info + Image */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                    <div
                        ref={infoRef}
                        className={`flex flex-col justify-center animate-on-scroll ${infoInView ? 'animate-fade-in-left' : ''}`}
                    >
                        <h2 className={`tracking-wider uppercase ${FONT_VARIANT} `}>Kontakt</h2>
                        <div className="w-20 h-px bg-white my-6"></div>
                        <p className="text-[#a19b92] mb-8">
                            Sie möchten sich verwöhnen lassen, Ihren Gästen etwas Besonderes bieten oder Ihre Kunden beeindrucken? Kontaktieren Sie uns – wir beraten Sie gerne und erstellen ein maßgeschneidertes Konzept für Ihre Veranstaltung in Nürnberg, Fürth und Erlangen!
                        </p>
                        <div className="space-y-6">
                            <div className="flex items-start">
                                <Phone className="mr-4 h-5 w-5 text-[#a19b92]" />
                                <div>
                                    <p className="font-medium">Telefon</p>
                                    <a
                                        href="tel:+4991337697956"
                                        className="text-[#a19b92] hover:text-white transition-colors"
                                    >
                                        Tel.: 09133 – 76 979 56
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <Mail className="mr-4 h-5 w-5 text-[#a19b92]" />
                                <div>
                                    <p className="font-medium">E-Mail</p>
                                    <a
                                        href="mailto:info@bachmann-catering.com"
                                        className="text-[#a19b92] hover:text-white transition-colors"
                                    >
                                        info@bachmann-catering.com
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <MapPin className="mr-4 h-5 w-5 text-[#a19b92]" />
                                <div>
                                    <p className="font-medium">Adresse</p>
                                    <a
                                        href="https://www.google.com/maps/search/?api=1&query=Erlanger+Str.+17+91083+Baiersdorf"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#a19b92] hover:text-white transition-colors"
                                    >
                                        Erlanger Str. 17, 91083 Baiersdorf
                                    </a>
                                </div>
                            </div>
                            <OpeningHours />
                        </div>
                        <div className="flex space-x-4 mt-8">
                            <a
                                href="https://instagram.com/bachmann_catering"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#a19b92] hover:text-white"
                                aria-label="Bachmann Catering auf Instagram besuchen (öffnet in neuem Tab)"
                            >
                                <Instagram className="h-6 w-6" />
                            </a>
                            <a
                                href="https://facebook.com/bachmann.catering"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#a19b92] hover:text-white"
                                aria-label="Bachmann Catering auf Facebook besuchen (öffnet in neuem Tab)"
                            >
                                <Facebook className="h-6 w-6" />
                            </a>
                        </div>
                    </div>
                    <div className={`relative w-full md:w-3/4 aspect-[3/4] overflow-hidden rounded-[3rem] animate-on-scroll ${infoInView ? 'animate-fade-in-right delay-300' : ''}`}>
                        <Image
                            src="/images/map.webp"
                            alt="Bachmann Catering Impression"
                            fill
                            className="object-cover transition-transform duration-700 hover:scale-105"
                            sizes="(max-width: 768px) 100vw, 50vw"
                            priority
                        />
                    </div>
                </div>

                {/* Second Row: Contact Form */}
                <div className="flex justify-center">
                    <div
                        ref={formRef}
                        className={`w-full md:w-2/3 animate-on-scroll ${formInView ? 'animate-fade-in-up delay-400' : ''}`}
                    >
                        <div className="font-quicksand mb-12">
                            <h2 className={`uppercase tracking-[0.2em] leading-[1.1] select-none text-white text-center ${FONT_VARIANT}`} >
                                Lust auf Genuss?
                                <span className="sr-only">
                                    Bachmann Catering-Kontakt
                                </span>
                                <span className="block text-base">Wir freuen uns auf Ihre Anfrage</span>
                            </h2>
                        </div>
                        <form action={async (formData: FormData) => {
                            try {
                                const result = await sendContactForm({
                                    company: formData.get('company') as string,
                                    firstname: formData.get('firstname') as string,
                                    lastname: formData.get('lastname') as string,
                                    street: formData.get('street') as string,
                                    city: formData.get('city') as string,
                                    phone: formData.get('phone') as string,
                                    eventType: formData.get('eventType') as string,
                                    date: formData.get('date') as string,
                                    guests: formData.get('guests') as string,
                                    email: formData.get('email') as string,
                                    budget: formData.get('budget') as string || undefined,
                                    fullService: formData.get('fullService') === 'on',
                                    speiseWuensche: formData.get('speiseWuensche') as string || undefined,
                                    message: formData.get('message') as string,
                                    website: formData.get('website') as string || undefined,
                                    formStartedAt: formData.get('formStartedAt') as string || undefined,
                                })

                                if (result.success) {
                                    toast({
                                        title: "Anfrage erfolgreich gesendet",
                                        description: "Vielen Dank für Ihre Anfrage. Wir werden uns schnellstmöglich bei Ihnen melden.",
                                        className: "bg-green-50 border-green-200",
                                    })
                                } else {
                                    toast({
                                        title: "Fehler beim Senden",
                                        description: result.error || "Bitte versuchen Sie es später erneut.",
                                        variant: "destructive",
                                    })
                                }
                            } catch (error) {
                                toast({
                                    title: "Fehler beim Senden",
                                    description: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
                                    variant: "destructive",
                                })
                            }
                        }} className="space-y-6">
                            {/* Anti-spam: invisible to humans, bots fill it. Inline styles so it never depends on CSS loading. */}
                            <div style={{ position: 'absolute', left: '-9999px', top: 0, width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                                <label htmlFor="website">Website</label>
                                <input
                                    id="website"
                                    name="website"
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                />
                            </div>
                            <input type="hidden" name="formStartedAt" value={formStartedAt} />
                            <div>
                                <label htmlFor="company" className="sr-only">Firma</label>
                                <input
                                    id="company"
                                    name="company"
                                    className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white"
                                    placeholder="Firma"
                                    defaultValue={process.env.NODE_ENV === 'development' ? 'Test GmbH' : ''}
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="firstname" className="sr-only">Vorname</label>
                                    <input
                                        id="firstname"
                                        name="firstname"
                                        className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white"
                                        placeholder="Vorname"
                                        required
                                        defaultValue={process.env.NODE_ENV === 'development' ? 'Max' : ''}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="lastname" className="sr-only">Nachname</label>
                                    <input
                                        id="lastname"
                                        name="lastname"
                                        className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white"
                                        placeholder="Nachname"
                                        required
                                        defaultValue={process.env.NODE_ENV === 'development' ? 'Mustermann' : ''}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <input
                                        name="street"
                                        className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white"
                                        placeholder="Straße, Hausnummer"
                                        required
                                        defaultValue={process.env.NODE_ENV === 'development' ? 'Teststraße 123' : ''}
                                    />
                                </div>
                                <div>
                                    <input
                                        name="city"
                                        className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white"
                                        placeholder="PLZ, Stadt"
                                        required
                                        defaultValue={process.env.NODE_ENV === 'development' ? '91052 Erlangen' : ''}
                                    />
                                </div>
                            </div>
                            <div>
                                <input
                                    name="phone"
                                    className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white"
                                    placeholder="Ihre Telefonnummer"
                                    required
                                    defaultValue={process.env.NODE_ENV === 'development' ? '+49 123 4567890' : ''}
                                />
                            </div>
                            <div>
                                <input
                                    name="eventType"
                                    className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white"
                                    placeholder="Art der Veranstaltung*"
                                    required
                                    defaultValue={process.env.NODE_ENV === 'development' ? 'Firmenevent' : ''}
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <input
                                        name="date"
                                        type="text"
                                        className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white"
                                        placeholder="Datum des Events*"
                                        required
                                        defaultValue={process.env.NODE_ENV === 'development' ? '01.12.2024' : ''}
                                    />
                                </div>
                                <div>
                                    <input
                                        name="guests"
                                        type="number"
                                        className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white"
                                        placeholder="Personenzahl*"
                                        required
                                        defaultValue={process.env.NODE_ENV === 'development' ? '50' : ''}
                                    />
                                </div>
                            </div>
                            <div>
                                <input
                                    name="email"
                                    type="email"
                                    className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white"
                                    placeholder="E-Mail*"
                                    required
                                    defaultValue={process.env.NODE_ENV === 'development' ? 'test@example.com' : ''}
                                />
                            </div>
                            <div>
                                <label htmlFor="budget" className="sr-only">Budget (optional)</label>
                                <select
                                    id="budget"
                                    name="budget"
                                    className="w-full bg-black border-b border-[#a19b92] px-0 py-2 focus:outline-none focus:border-white text-[#a19b92]"
                                >
                                    <option value="" className="bg-black text-[#a19b92]">Budget (optional)</option>
                                    <option value="unter-5000" className="bg-black text-white">Unter 5.000 €</option>
                                    <option value="5000-10000" className="bg-black text-white">5.000 - 10.000 €</option>
                                    <option value="10000-25000" className="bg-black text-white">10.000 - 25.000 €</option>
                                    <option value="25000-50000" className="bg-black text-white">25.000 - 50.000 €</option>
                                    <option value="ueber-50000" className="bg-black text-white">Über 50.000 €</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm text-[#a19b92] mb-4">
                                    <input
                                        type="checkbox"
                                        name="fullService"
                                        className="mr-2"
                                        defaultChecked={process.env.NODE_ENV === 'development'}
                                    />
                                    Full-Service gewünscht? (Mobiliar, Deko, Technik, Getränke etc.)
                                </label>
                            </div>
                            <div>
                                <textarea
                                    name="speiseWuensche"
                                    className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white min-h-[80px]"
                                    placeholder="Speisewünsche (Freitext)"
                                    defaultValue={process.env.NODE_ENV === 'development' ? 'Vegetarische Optionen bevorzugt' : ''}
                                ></textarea>
                            </div>
                            <div>
                                <textarea
                                    name="message"
                                    className="w-full bg-black border-b border-[#a19b92] px-0 py-2 text-white placeholder-[#a19b92] focus:outline-none focus:border-white min-h-[120px]"
                                    placeholder="Anmerkungen (Freitext)"
                                    defaultValue={process.env.NODE_ENV === 'development' ? 'Dies ist eine Testanfrage für ein Firmenevent mit Catering-Service.' : ''}
                                ></textarea>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-start gap-2">
                                    <input
                                        type="checkbox"
                                        name="agb"
                                        id="agb"
                                        className="mt-1"
                                        required
                                        defaultChecked={process.env.NODE_ENV === 'development'}
                                    />
                                    <label htmlFor="agb" className="text-sm text-[#a19b92]">
                                        Ich stimme den <a href="/agb" className="underline hover:text-white">AGB</a> und der <a href="/datenschutz" className="underline hover:text-white">Datenschutzerklärung</a> zu.*
                                    </label>
                                </div>
                                <div className="flex items-start gap-2">
                                    <input
                                        type="checkbox"
                                        name="processingFee"
                                        id="processingFee"
                                        className="mt-1"
                                        required
                                        defaultChecked={process.env.NODE_ENV === 'development'}
                                    />
                                    <label htmlFor="processingFee" className="text-sm text-[#a19b92]">
                                        Für ein persönlich zugeschnittenes Angebot fallen 25 € Bearbeitungsgebühr bei nicht Annahme des Auftrages an.
                                    </label>
                                </div>
                            </div>
                            <SubmitButton />
                            <p className="mt-4 text-xs text-[#a19b92]">
                                Mit * markierte Felder sind Pflichtangaben. Wir melden uns in der Regel innerhalb von 24 Stunden. Ihre Daten sind bei uns sicher und werden ausschließlich zur Bearbeitung Ihrer Anfrage genutzt.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}